import { useCallback, useEffect, useState } from 'react';

import { actionError } from '../components/actionError.ts';

export type LoadState = { kind: 'loading' } | { kind: 'ready' } | { kind: 'error'; message: string };

export interface UseLoadStateResult<T> {
  /** The latest successfully loaded value. Stays at `initial` until the
   *  first load resolves, and keeps its last value through a later reload's
   *  loading/error phases rather than flashing empty. */
  data: T;
  state: LoadState;
  /** Re-runs `fetcher` and applies its result. Resolves to the freshly
   *  loaded value, or undefined if the fetch failed (either way `state`
   *  reflects the outcome). */
  reload: () => Promise<T | undefined>;
}

export function useLoadState<T>(fetcher: () => Promise<T>, initial: T): UseLoadStateResult<T> {
  const [data, setData] = useState<T>(initial);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const reload = useCallback((): Promise<T | undefined> => {
    setState({ kind: 'loading' });
    return fetcher()
      .then((result) => {
        setData(result);
        setState({ kind: 'ready' });
        return result;
      })
      .catch((e: unknown) => {
        setState({ kind: 'error', message: actionError(e) });
        return undefined;
      });
  }, [fetcher]);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    fetcher()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setState({ kind: 'ready' });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', message: actionError(e) });
      });
    return () => {
      cancelled = true;
    };
    // Re-runs whenever the caller's fetcher identity changes (e.g. a filter
    // toggle rebuilding its useCallback), same as the page-local effects
    // this hook replaces.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher]);

  return { data, state, reload };
}

export default useLoadState;
